/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import service from '../../services/http';
import runAnalysisPayload from "../../mocks/run-analysis-payload.json";
import { ItpExecutionStatuses } from '@xbs/xbs-enums';
import { formatRateLimitHeaderIntoMessage } from '../../utils/rateLimitHeaderParser';
import { FetchLoadingStateEnum, HTTP_CODES } from '../../constants';
import { getDocumentSetting, setDocumentSetting } from '../../components/exactera/api';
import { wordSetting } from '../../models/wordStorageData';
import { AxiosError } from "axios";
import { DocumentSettings, DocumentSettingValueType } from '../../models/documentSettings';
import { SuggestionIconType } from '../../models/suggestionStatus.interface';

export type FetchLoadingState =
  | FetchLoadingStateEnum.loading
  | FetchLoadingStateEnum.fulfilled
  | FetchLoadingStateEnum.error

export enum SuggestionStatusEnum {
  ToReview = 'toReview',
  Completed = 'completed',
  Ignored = 'ignored'
}
export interface Suggestion {
  promptIdentifier: string;
  message: string;
  citations: Citations[];
  citationsFromLlm: string[];
  status?: SuggestionStatusEnum | null;
}

export interface Citations {
  text: string;
  heading: string;
}

export interface Jurisdiction {
  id: string;
  name: string;
}

export type Suggestions = Suggestion[];

interface AnalyzeReportStatus {
  status: string;
}

interface ItpExecutionIdParams {
  itpExecutionId: string;
  useMockResponse: boolean;
}

interface AnalysisReportParams extends ItpExecutionIdParams {
  data: BodyInit;
  contentType: string;
  filename: string;
}

interface LoadExecutionsResult {
  body: string;
}

export interface ReportBackgroundData {
  jurisdiction: string;
  taxYear: number;
}

export interface S3DataResult {
  suggestions: Suggestions;
  backgroundData: ReportBackgroundData;
}

export interface GetDocumentPropertyNameFromSettingResult {
  propertyName: string;
  value: DocumentSettingValueType | null;
}

export interface SuggestionsState {
  executionId?: string;
  suggestions: Suggestions;
  reportBackgroundData?: ReportBackgroundData;
  status?: string;
  signedUrl?: string;
  suggestionCommentsCount: number;
  loading: {
    loadingPreviousAnalysisStatus?: FetchLoadingState;
    analyzeReport?: FetchLoadingState;
    fetchAnalyzeReportStatus?: FetchLoadingState;
    loadAnalyzeReportExecutions?: FetchLoadingState;
    fetchDataFromS3?: FetchLoadingState;
  };
  flags: {
    useMockResponse: boolean;
  };
  analysisErrorMessage: string;
  documentId: string;
  documentSettings: DocumentSettings;
  activeJurisdiction: string | null;
  jurisdictions: Jurisdiction[];
}

const initialState: SuggestionsState = {
  suggestions: [],
  suggestionCommentsCount: 0,
  loading: {
    fetchAnalyzeReportStatus: null,
    loadingPreviousAnalysisStatus: null
  },
  flags: {
    useMockResponse: false
  },
  analysisErrorMessage: 'Error during Analysis, please try again.',
  documentId: '',
  documentSettings: {
    isLoading: null
  },
  activeJurisdiction: null,
  jurisdictions: [],
};


export interface SetSuggestionStatusPayload {
  id: string;
  status: SuggestionIconType;
}

export const loadPreviousAnalysis = createAsyncThunk(
  'load-word-document-stored-analysis',
  async (_, { rejectWithValue }) => {
    try {
      const suggestionsDefaultValue = [];
      const [itpExecutionId, suggestions, backgroundData] = await Promise.all([
        getDocumentSetting('itpExecutionId'),
        getDocumentSetting('suggestions', suggestionsDefaultValue),
        getDocumentSetting('backgroundData')
      ]);
      return { suggestions, itpExecutionId, backgroundData };
    } catch (error: unknown) {
      return rejectWithValue(error as Error);
    }
  });

export const loadSuggestions = createAsyncThunk(
  'load-word-document-stored-suggestions',
  async (_, { rejectWithValue }) => {
    try {
      const suggestionsDefaultValue = [];
      const suggestions = await getDocumentSetting('suggestions', suggestionsDefaultValue);
      return suggestions;
    } catch (error: unknown) {
      return rejectWithValue(error as Error);
    }
  });

export const fetchJurisdictions = createAsyncThunk<Jurisdiction[], void, { rejectValue: Error }>(
  'v1/jurisdictions',
  async (_, { rejectWithValue }) => {
    try {
      const res = await service.request<{ data: Jurisdiction[] }>({
        method: 'get',
        apiUrlKey: 'itpUrl',
        relativePath: '/v1/jurisdictions',
      });
      return res.data.data;
    } catch (error: unknown) {
      return rejectWithValue(error as Error);
    }
  }
);

export const analyzeReport = createAsyncThunk<void, AnalysisReportParams, { rejectValue: Error }>(
  'v1/analyze-report',
  async ({ data, itpExecutionId, contentType, filename, useMockResponse }, { rejectWithValue }) => {
    try {
      let response;
      if (!useMockResponse) {
        const res = await service.request<{ data: any }>({
          method: 'post',
          apiUrlKey: 'itpUrl',
          relativePath: `/v1/analyze-report`,
          data,
          headers: { 'itp-execution-id': itpExecutionId, 'Content-Type': contentType, 'file-name': filename }
        });
        response = res.data.data;
      } else {
        response = { code: 200 };
      }
      return response;
    } catch (error: unknown) {
      return rejectWithValue(error as Error);
    }
  }
);

let repeatMockFetchCounter = 3;

export const fetchAnalyzeReportStatus = createAsyncThunk<AnalyzeReportStatus, ItpExecutionIdParams, { rejectValue: Error }>(
  'v1/analyze-report/status',
  async ({ itpExecutionId, useMockResponse }, { rejectWithValue }) => {
    try {
      let response: AnalyzeReportStatus;
      if (useMockResponse) {
        if (repeatMockFetchCounter-- > 0) {
          response = { status: ItpExecutionStatuses.ByName.Pending.Name };
        } else {
          repeatMockFetchCounter = 3;
          response = { status: ItpExecutionStatuses.ByName.Success.Name };
        }
      } else {
        const res = await service.request<{ data: AnalyzeReportStatus }>({
          method: 'get',
          apiUrlKey: 'itpUrl',
          relativePath: `/v1/analyze-report/status`,
          headers: { 'itp-execution-id': itpExecutionId },
        });
        response = res.data.data;
      }

      return response;
    } catch (error: unknown) {
      return rejectWithValue(error as Error);
    }
  }
);

export const loadAnalyzeReportExecutions = createAsyncThunk<LoadExecutionsResult, ItpExecutionIdParams, { rejectValue: Error }>('v1/analyze-report/load-executions',
  async ({ itpExecutionId, useMockResponse }, { rejectWithValue }) => {
    try {
      let response: LoadExecutionsResult;
      if (!useMockResponse) {
        const res = await service.request<{ data: LoadExecutionsResult }>({
          method: 'get',
          apiUrlKey: 'itpUrl',
          relativePath: `/v1/analyze-report/load-executions`,
          headers: { 'itp-execution-id': itpExecutionId },
        });
        response = res.data.data;
      } else {
        response = { body: 'fake-s3-signed-url' };
      }

      return response;
    } catch (error: unknown) {
      return rejectWithValue(error as Error);
    }
  }
);

export const fetchDataFromS3 = createAsyncThunk<S3DataResult, { signedUrl: string, useMockResponse: boolean }, { rejectValue: Error }>(
  'v1/analyze-report/fetch-data-from-s3',
  async ({ signedUrl, useMockResponse }, { rejectWithValue }) => {
    try {
      let response: S3DataResult;
      if (!useMockResponse) {
        const res = await service.requestWithoutAuthHeader<S3DataResult>({
          method: 'get',
          relativePath: signedUrl,
        });

        response = res.data;
      } else {
        response = runAnalysisPayload;
      }
      return {
        ...response,
        suggestions: response.suggestions.map((suggestion) => ({
          ...suggestion,
          status: suggestion?.status as SuggestionStatusEnum ?? SuggestionStatusEnum.ToReview
        }))
      };
    } catch (error: unknown) {
      return rejectWithValue(error as Error);
    }
  });

export const setSuggestionsToWordDocument = createAsyncThunk<any, { wordSettings: wordSetting[] }, { rejectValue: Error }>(
  'set-suggestions-to-word-document',
  async ({ wordSettings }, { rejectWithValue }) => {
    try {
      const promises = [];
      for (const index in wordSettings) {
        promises.push(setDocumentSetting(wordSettings[index].settingName, wordSettings[index].settingValue));
      }
      const response = await Promise.all(promises);

      return response;
    } catch (error: unknown) {
      return rejectWithValue(error as Error);
    }
  });

export const getDocumentPropertyNameFromSetting = createAsyncThunk<GetDocumentPropertyNameFromSettingResult, string, { rejectValue: Error }>(
  'get-document-property-name-from-setting',
  async (propertyName, { rejectWithValue }) => {
    try {
      const value = await getDocumentSetting(propertyName, null);
      return { propertyName, value };
    } catch (error: unknown) {
      return rejectWithValue(error as Error);
    }
  }
);

export const setSuggestionStatus = createAsyncThunk<SetSuggestionStatusPayload, SetSuggestionStatusPayload, { rejectValue: Error }>(
  'set-suggestion-status',
  async (status, { rejectWithValue }) => {
    try {
      return status;
    } catch (error: unknown) {
      return rejectWithValue(error as Error);
    }
  }
);

export const suggestionsSlice = createSlice({
  name: 'suggestions',
  initialState,
  reducers: {
    prepareStateForNewAnalysis: (state) => {
      state.analysisErrorMessage = 'Error during Analysis, please try again.';
      state.executionId = null;
      state.loading = { fetchAnalyzeReportStatus: FetchLoadingStateEnum.loading, loadingPreviousAnalysisStatus: null };
      state.reportBackgroundData = null;
      state.signedUrl = null;
      state.status = null;
      state.suggestionCommentsCount = 0;
      state.suggestions = [];
    },
    setExecutionId: (state, action) => {
      state.executionId = action.payload;
    },
    setAnalyzeReportResults: (state, action: PayloadAction<S3DataResult>) => {
      state.suggestions = action.payload.suggestions;
      state.reportBackgroundData = action.payload.backgroundData;
    },
    setSuggestionCommentsCount: (state, action: PayloadAction<number>) => {
      state.suggestionCommentsCount = action.payload;
    },
    setAnalyzeReportStatusLoadingState: (state, action: PayloadAction<FetchLoadingState>) => {
      state.loading.fetchAnalyzeReportStatus = action.payload;
    },
    setAnalyzeReportStatus: (state, action: PayloadAction<FetchLoadingState>) => {
      state.loading.analyzeReport = action.payload;
    },
    setLoadPreviousAnalysisStatus: (state, action: PayloadAction<FetchLoadingState>) => {
      state.loading.loadingPreviousAnalysisStatus = action.payload;
    },
    setUseMockResponseState: (state, action: PayloadAction<boolean>) => {
      state.flags.useMockResponse = action.payload;
    },
    setDocumentId: (state, action: PayloadAction<string>) => {
      state.documentId = action.payload;
    },
    setActiveJurisdiction: (state, action: PayloadAction<string | null>) => {
      state.activeJurisdiction = action.payload;
    },
    setDocumentSettings: (state, action: PayloadAction<GetDocumentPropertyNameFromSettingResult>) => {
      const { propertyName, value } = action.payload;
      state.documentSettings[propertyName] = value;
    },
    setSuggestionStatus: (state, action: PayloadAction<SetSuggestionStatusPayload>) => {
      const { id, status } = action.payload;

      const mapper = {
        [SuggestionIconType.Completed]: SuggestionStatusEnum.Completed,
        [SuggestionIconType.Ignored]: SuggestionStatusEnum.Ignored
      };

      state.suggestions = state.suggestions.map((suggestion) => {
        if (suggestion.promptIdentifier === id) {
          if (suggestion.status?.toString() === status.toString()) {
            return { ...suggestion, status: SuggestionStatusEnum.ToReview };
          }
          return { ...suggestion, status: mapper[status] };
        }
        return suggestion;
      });
    }
  },

  extraReducers(builder) {
    builder
      .addCase(analyzeReport.pending, (state: SuggestionsState, action: any) => {
        state.executionId = action.meta.arg.itpExecutionId;
      })
      .addCase(analyzeReport.rejected, (state: SuggestionsState, rejectValue) => {
        const error = rejectValue.payload;
        if (error instanceof AxiosError && error.status === HTTP_CODES.RateLimit) {
          const rateLimit = error.response.headers['ratelimit'];
          state.analysisErrorMessage = formatRateLimitHeaderIntoMessage(rateLimit);
        }

        state.loading = { fetchAnalyzeReportStatus: FetchLoadingStateEnum.error };
        state.documentSettings.isLoading = false;
      })
      .addCase(setSuggestionStatus.fulfilled, (state: SuggestionsState, action: any) => {
        // setSuggestionStatus: (state, action: PayloadAction<SetSuggestionStatusPayload>) => {
        const { id, status } = action.payload;

        const mapper = {
          [SuggestionIconType.Completed]: SuggestionStatusEnum.Completed,
          [SuggestionIconType.Ignored]: SuggestionStatusEnum.Ignored
        };

        state.suggestions = state.suggestions.map((suggestion) => {
          if (suggestion.promptIdentifier === id) {
            if (suggestion.status?.toString() === status.toString()) {
              return { ...suggestion, status: SuggestionStatusEnum.ToReview };
            }
            return { ...suggestion, status: mapper[status] };
          }
          return suggestion;
        });
      })
      .addCase(fetchJurisdictions.fulfilled, (state: SuggestionsState, action) => {
        state.jurisdictions = action.payload;
      })
      .addCase(loadSuggestions.fulfilled, (state: SuggestionsState, action: any) => {
        state.suggestions = action.payload;
      });
  }
});

export const {
  prepareStateForNewAnalysis,
  setAnalyzeReportResults,
  setAnalyzeReportStatusLoadingState,
  setLoadPreviousAnalysisStatus,
  setAnalyzeReportStatus,
  setActiveJurisdiction,
  setExecutionId,
  setSuggestionCommentsCount,
  setUseMockResponseState,
  setDocumentId,
  setDocumentSettings,
} = suggestionsSlice.actions;

export default suggestionsSlice.reducer;